'use client'

import { resolvePageReadinessState } from '@brew/observability/page-readiness-state'
import { useMemo, ViewTransition } from 'react'
import { BlurFade } from '@/components/ui/blur-fade'
import { useBrandContext } from '@/contexts/brand-context'
import { useOrgContext } from '@/contexts/org-context'
import { api } from '@/convex/_generated/api'
import { usePageContentReadiness } from '@/hooks/use-page-content-readiness'
import { useSeededAuthedQuery } from '@/hooks/use-seeded-authed-query'
import type { QuerySeed } from '@/lib/data/convex/query-seed'
import { TEMPLATE_GRID_CLASSES } from '../lib/grid-classes'
import { useTemplatesBasePath } from '../lib/templates-surface-context'
import type { TemplateSnapshot } from '../lib/types'
import { buildTemplateEmailPath } from '../lib/url-helpers'
import { EmailPreviewCard } from './template-card'
import { TemplateCardSkeleton } from './template-grid'
import { SentEmptyState } from './templates-sent-empty-state'

const SKELETON_KEYS = Array.from({ length: 6 }, (_, i) => `sent-skel-${i}`)

/** The templates page's server read of the Sent collection. */
export type SentDesignsSeed = QuerySeed<
  typeof api.analytics.brand_sent_designs.listBrandSentDesignsByOpens
>

function toSnapshot(row: {
  emailId: string
  title: string
  category?: string
  imagePngUrl?: string
  brandDomain?: string
  status?: TemplateSnapshot['status']
  openedCount: number
}): TemplateSnapshot {
  return {
    emailId: row.emailId,
    title: row.title,
    category: row.category,
    imagePngUrl: row.imagePngUrl,
    brandDomain: row.brandDomain,
    status: row.status,
    openedCount: row.openedCount,
  }
}

function SentGridSkeleton() {
  return (
    <ViewTransition exit="slide-down">
      <div className={TEMPLATE_GRID_CLASSES}>
        {SKELETON_KEYS.map((key) => (
          <TemplateCardSkeleton key={key} />
        ))}
      </div>
    </ViewTransition>
  )
}

function SentEmailCard({
  snapshot,
  index,
  href,
}: {
  snapshot: TemplateSnapshot
  index: number
  href: string
}) {
  return (
    <ViewTransition default="none">
      <div className="min-w-0">
        <BlurFade inView delay={(index % 6) * 0.06}>
          <EmailPreviewCard snapshot={snapshot} href={href} />
        </BlurFade>
      </div>
    </ViewTransition>
  )
}

export function SentEmailGrid({
  seed = null,
}: {
  /** Painted until the subscription answers (same org and brand only). */
  seed?: SentDesignsSeed | null
}) {
  const basePath = useTemplatesBasePath()
  const { orgId } = useOrgContext()
  const { brandId } = useBrandContext()

  const rows = useSeededAuthedQuery(
    api.analytics.brand_sent_designs.listBrandSentDesignsByOpens,
    orgId && brandId ? { orgId, brandId } : 'skip',
    seed
  )

  const emails = useMemo(() => (rows ?? []).map(toSnapshot), [rows])
  usePageContentReadiness(
    ['web_templates', 'web_browse_templates'],
    resolvePageReadinessState({
      loading: rows === undefined,
      empty: emails.length === 0,
    }),
    { scope: brandId }
  )
  if (rows === undefined) {
    return <SentGridSkeleton />
  }

  if (emails.length === 0) {
    return (
      <ViewTransition enter="slide-up" default="none">
        <SentEmptyState />
      </ViewTransition>
    )
  }

  return (
    <ViewTransition enter="slide-up" default="none">
      <div className={TEMPLATE_GRID_CLASSES}>
        {emails.map((snapshot, index) => (
          <SentEmailCard
            key={snapshot.emailId}
            snapshot={snapshot}
            index={index}
            href={buildTemplateEmailPath(snapshot.emailId, basePath)}
          />
        ))}
      </div>
    </ViewTransition>
  )
}
